import * as React from "react";
import { Button } from "antd";
import { observer } from "mobx-react-lite";
import AddProjectForm from "../../administration/components/AddProjectForm";
import AppModal from "../../common/components/AppModal";
import { ProjectsStore } from "../../common/stores";
import TaskTemplateService from "../../task_templates/services/TaskTemplateService";
import { TaskTemplate } from "../../task_templates/types";
import TaskTypesService from "../../task_types/services/TaskTypesService";
import { TaskType } from "../../task_types/types";

type Props = {
    projectsStore: ProjectsStore;
    isDialogVisible: boolean;
    closeDialog: () => void;
};

type ProjectContentSummary = {
    customTaskTypesCount: number;
    templatesCount: number;
};

const AddProjectDialog: React.FC<Props> = ({
    projectsStore,
    isDialogVisible,
    closeDialog,
}) => {
    const [projectContentSummary, setProjectContentSummary] =
        React.useState<ProjectContentSummary | null>(null);

    const createUpdateProject = async (
        name: string,
        owner: string | undefined,
        enrichmentEnabled: boolean,
    ) => {
        await projectsStore.createUpdateProject(
            name,
            owner,
            enrichmentEnabled,
            () => closeDialog(),
        );
    };

    const handleCancel = () => {
        closeDialog();
    };

    React.useEffect(() => {
        if (
            !projectsStore.editableProject ||
            !projectsStore.editableProjectId
        ) {
            setProjectContentSummary(null);
            return;
        }

        let isMounted = true;
        const taskTypesService = new TaskTypesService();
        const taskTemplateService = new TaskTemplateService();

        Promise.all([
            taskTypesService.getTaskTypesForProject(
                projectsStore.editableProjectId,
            ),
            taskTemplateService.getTaskTemplates(
                projectsStore.editableProjectId,
            ),
        ])
            .then(([taskTypesResponse, templatesResponse]) => {
                if (!isMounted) {
                    return;
                }

                let taskTypes: TaskType[] = [];
                let templates: TaskTemplate[] = [];

                taskTypesResponse
                    .map((items) => {
                        taskTypes = items ?? [];
                        return items;
                    })
                    .mapErr(() => undefined);

                templatesResponse
                    .map((items) => {
                        templates = items ?? [];
                        return items;
                    })
                    .mapErr(() => undefined);

                setProjectContentSummary({
                    customTaskTypesCount: taskTypes.filter(
                        (taskType) => !taskType.isSystemDefault,
                    ).length,
                    templatesCount: templates.length,
                });
            })
            .catch(() => {
                if (isMounted) {
                    setProjectContentSummary(null);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [projectsStore.editableProject, projectsStore.editableProjectId]);

    const customTaskTypesCount =
        projectContentSummary?.customTaskTypesCount ?? 0;
    const templatesCount = projectContentSummary?.templatesCount ?? 0;
    const shouldShowProjectContentInfo =
        !!projectsStore.editableProject &&
        (customTaskTypesCount > 0 || templatesCount > 0);

    const summaryParts = [
        customTaskTypesCount > 0
            ? `${customTaskTypesCount} custom task ${customTaskTypesCount === 1 ? "type" : "types"}`
            : null,
        templatesCount > 0
            ? `${templatesCount} template${templatesCount === 1 ? "" : "s"}`
            : null,
    ].filter(Boolean) as string[];

    const handleManageTaskTypesClick = () => {
        if (!projectsStore.editableProjectId) {
            return;
        }

        window.open(
            `/task/types?projectId=${projectsStore.editableProjectId}`,
            "_blank",
            "noopener,noreferrer",
        );
    };

    const handleManageTemplatesClick = () => {
        if (!projectsStore.editableProjectId) {
            return;
        }

        window.open(
            `/task/tasktemplates?projectId=${projectsStore.editableProjectId}`,
            "_blank",
            "noopener,noreferrer",
        );
    };

    return (
        <AppModal
            centered
            title={
                projectsStore.editableProject
                    ? "Edit project"
                    : "Create project"
            }
            infoBanner={
                projectsStore.editableProject
                    ? shouldShowProjectContentInfo
                        ? {
                              description: (
                                  <div className="create-project-modal__summary-info">
                                      <div className="create-project-modal__summary-line">
                                          Configured: {summaryParts.join(" · ")}
                                      </div>
                                      <div className="create-project-modal__summary-links">
                                          {customTaskTypesCount > 0 ? (
                                              <Button
                                                  type="link"
                                                  className="create-project-modal__summary-link"
                                                  onClick={
                                                      handleManageTaskTypesClick
                                                  }
                                              >
                                                  Manage task types
                                              </Button>
                                          ) : null}
                                          {customTaskTypesCount > 0 &&
                                          templatesCount > 0 ? (
                                              <span className="create-project-modal__summary-separator">
                                                  ·
                                              </span>
                                          ) : null}
                                          {templatesCount > 0 ? (
                                              <Button
                                                  type="link"
                                                  className="create-project-modal__summary-link"
                                                  onClick={
                                                      handleManageTemplatesClick
                                                  }
                                              >
                                                  Manage templates
                                              </Button>
                                          ) : null}
                                      </div>
                                  </div>
                              ),
                          }
                        : undefined
                    : {
                          description:
                              "A standard project is ready to use with Open, In Progress, Blocked, and Done statuses.",
                      }
            }
            infoBannerPlacement="bottom"
            destroyOnClose
            closable={false}
            maskClosable={false}
            open={isDialogVisible}
            className="alpha-modal create-project-modal"
            afterClose={() => projectsStore.setEditableProject(null)}
            cancelButtonTitle="Cancel"
            onCancelClick={handleCancel}
            cancelButtonProps={{
                "data-id": "add-project-cancel",
            }}
            submitButtonTitle={
                projectsStore.editableProject ? "Save" : "Create"
            }
            submitButtonProps={{
                "data-id": "add-project-submit",
                form: "add-project-form",
                loading: projectsStore.isProjectUpdateLoading,
            }}
        >
            <AddProjectForm
                onAddProject={createUpdateProject}
                store={projectsStore}
            />
        </AppModal>
    );
};

export default observer(AddProjectDialog);
